import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus, ChevronRight, ChevronLeft, ShieldCheck, Save, Check } from "lucide-react";
import PaymentStep from "@/components/PaymentStep";
import { credentialLabel } from "@/lib/labels";

// IBTHM-only — institutional accreditation lives at /dashboard/schools/new.
// Practitioner-facing options (UAT: no ACIS codes here).
const IBTHM_CRED_TYPES = ["BCHHP", "BCT", "BCDTS"];

const STEPS = ["Payment", "Credential", "Personal info", "Education", "Experience", "Declarations & submit"];

const EMPTY_FORM = {
  organization: "IBTHM",
  credential_type: "",
  primary_school_id: "",
  personal_info: {
    full_name: "", date_of_birth: "", address: "", city: "", state: "", country: "", phone: "",
  },
  education: [{ institution: "", degree: "", year: "", hours: "" }],
  experience: [{ employer: "", role: "", years: "", description: "" }],
  declarations: { truth: false, ethics: false, disciplinary: false, disciplinary_note: "" },
};

export default function ApplicationNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const resumeId = sp.get("draft");
  const [step, setStep] = useState(0);
  const [paid, setPaid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    api.get("/schools/public").then((r) => setSchools(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/drafts/${resumeId}`);
        setDraftId(data.id);
        setForm({ ...EMPTY_FORM, ...(data.payload || {}) });
        setLastSavedAt(data.last_saved_at);
        toast.success("Draft restored");
      } catch (e) {
        toast.error(errMessage(e));
      }
    })();
  }, [resumeId]);

  const saveDraft = async (silent = false) => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/drafts", {
        track: "ibthm_app",
        payload: form,
        credential_type: form.credential_type,
        note: `${form.credential_type || "IBTHM"} application — ${form.personal_info?.full_name || "Untitled"}`,
      });
      setDraftId(data.id);
      setLastSavedAt(data.last_saved_at);
      dirtyRef.current = false;
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      if (!silent) toast.error(errMessage(e));
    } finally {
      setSavingDraft(false);
    }
  };

  // Auto-save every 60s
  useEffect(() => {
    const id = setInterval(() => {
      if (dirtyRef.current) saveDraft(true);
    }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const set = (path, value) => {
    dirtyRef.current = true;
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateArray = (key, idx, field, value) => {
    setForm((prev) => {
      const list = [...prev[key]];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, [key]: list };
    });
  };
  const addItem = (key, template) => setForm((p) => ({ ...p, [key]: [...p[key], template] }));
  const delItem = (key, idx) => setForm((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  const canNext = () => {
    if (step === 0) return paid;
    if (step === 1) return Boolean(form.credential_type);
    if (step === 2) {
      const p = form.personal_info;
      return p.full_name && p.country;
    }
    if (step === 3) return Boolean(form.primary_school_id);
    if (step === 5) return form.declarations.truth && form.declarations.ethics;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", form);
      if (draftId) {
        try { await api.post(`/drafts/${draftId}/promote`); } catch { /* non-fatal */ }
      }
      toast.success("Application submitted. You can pay and upload documents from the application page.");
      nav(`/dashboard/applications/${data.id}`);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-up" data-testid="application-new">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">IBTHM · Board Certification</div>
          <h1 className="font-heading text-3xl font-bold text-primary">New IBTHM Certification Application</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pay the application fee, then complete each step. Institutional accreditation
            (ACIS) is a separate workflow — start it from the <a href="/dashboard/schools/new" className="text-primary underline">Accreditation Applications</a> page.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lastSavedAt && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[11px]" data-testid="ibthm-last-saved">
              <Check className="h-3 w-3 mr-1" /> Saved {new Date(lastSavedAt).toLocaleTimeString()}
            </Badge>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => saveDraft(false)}
            disabled={savingDraft}
            data-testid="ibthm-save-draft-btn"
          >
            <Save className="h-4 w-4 mr-1" /> {savingDraft ? "Saving…" : "Save draft"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-3 p-5 border-slate-200 h-fit sticky top-24">
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s} className="flex items-start gap-3">
                <div className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border ${i === step ? "bg-primary text-white border-primary" : i < step ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-500 border-slate-300"}`}>
                  {i + 1}
                </div>
                <div className={`text-sm ${i === step ? "text-primary font-semibold" : "text-slate-600"}`}>{s}</div>
              </li>
            ))}
          </ol>
        </Card>

        <Card className="lg:col-span-9 p-7 border-slate-200">
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-primary">Step 1 — Application Fee</h2>
              <p className="text-sm text-slate-600">
                Pay the IBTHM Board Certification Application Fee to unlock the application form below.
              </p>
              <PaymentStep
                paymentType="ibthm_application_fee"
                entityType="application"
                onPaid={() => setPaid(true)}
              />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-primary">Choose your credential</h2>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                Issuing board: <span className="font-semibold text-primary">IBTHM · International Board of Trichology &amp; Holistic Medicine</span>
              </div>
              <div>
                <Label className="mb-1.5 block">Credential type</Label>
                <Select value={form.credential_type} onValueChange={(v) => set("credential_type", v)}>
                  <SelectTrigger data-testid="new-credtype-select"><SelectValue placeholder="Select credential" /></SelectTrigger>
                  <SelectContent>
                    {IBTHM_CRED_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {credentialLabel(t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.credential_type && (
                  <p className="text-xs text-slate-500 mt-2">
                    You selected <span className="font-semibold text-primary">{credentialLabel(form.credential_type)}</span>.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-primary mb-2">Personal information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full legal name" testId="pi-name" value={form.personal_info.full_name} onChange={(v) => set("personal_info.full_name", v)} required />
                <Field label="Date of birth" testId="pi-dob" type="date" value={form.personal_info.date_of_birth} onChange={(v) => set("personal_info.date_of_birth", v)} />
                <Field label="Phone" testId="pi-phone" value={form.personal_info.phone} onChange={(v) => set("personal_info.phone", v)} />
                <Field label="Country" testId="pi-country" value={form.personal_info.country} onChange={(v) => set("personal_info.country", v)} required />
                <Field label="State / Province" testId="pi-state" value={form.personal_info.state} onChange={(v) => set("personal_info.state", v)} />
                <Field label="City" testId="pi-city" value={form.personal_info.city} onChange={(v) => set("personal_info.city", v)} />
                <div className="sm:col-span-2">
                  <Label className="mb-1.5 block">Street address</Label>
                  <Input data-testid="pi-address" value={form.personal_info.address} onChange={(e) => set("personal_info.address", e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-primary">Education</h2>
                <Button variant="outline" size="sm" onClick={() => addItem("education", { institution: "", degree: "", year: "", hours: "" })} data-testid="add-edu-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add education
                </Button>
              </div>

              <Card className="p-5 border-slate-200 bg-slate-50">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Primary ACIS-accredited institution <span className="text-red-500">*</span></div>
                <p className="text-xs text-slate-600 mb-3">Your educational pathway must trace to an ACIS-accredited school. Select the institution that issued your primary qualifying credential.</p>
                <Select value={form.primary_school_id} onValueChange={(v) => set("primary_school_id", v)}>
                  <SelectTrigger data-testid="primary-school-select"><SelectValue placeholder="Pick your accredited school" /></SelectTrigger>
                  <SelectContent>
                    {schools.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} — {s.country}{s.state ? `, ${s.state}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.primary_school_id && (() => {
                  const s = schools.find((x) => x.id === form.primary_school_id);
                  return s ? (
                    <div className="mt-3 text-xs text-slate-600 flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="font-mono-data">{s.accreditation_number}</span>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 capitalize text-[10px]">
                        ACIS · {s.accreditation_status}
                      </Badge>
                    </div>
                  ) : null;
                })()}
              </Card>

              {form.education.map((edu, i) => (
                <Card key={i} className="p-5 border-slate-200">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Institution" testId={`edu-inst-${i}`} value={edu.institution} onChange={(v) => updateArray("education", i, "institution", v)} />
                    <Field label="Degree / program" testId={`edu-deg-${i}`} value={edu.degree} onChange={(v) => updateArray("education", i, "degree", v)} />
                    <Field label="Year completed" testId={`edu-year-${i}`} value={edu.year} onChange={(v) => updateArray("education", i, "year", v)} />
                    <Field label="Clinical / supervised hours" testId={`edu-hours-${i}`} value={edu.hours} onChange={(v) => updateArray("education", i, "hours", v)} />
                  </div>
                  {form.education.length > 1 && (
                    <Button variant="ghost" size="sm" className="text-red-600 mt-3" onClick={() => delItem("education", i)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold text-primary">Professional experience</h2>
                <Button variant="outline" size="sm" onClick={() => addItem("experience", { employer: "", role: "", years: "", description: "" })} data-testid="add-exp-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add experience
                </Button>
              </div>
              {form.experience.map((ex, i) => (
                <Card key={i} className="p-5 border-slate-200">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Employer / clinic" testId={`exp-emp-${i}`} value={ex.employer} onChange={(v) => updateArray("experience", i, "employer", v)} />
                    <Field label="Role" testId={`exp-role-${i}`} value={ex.role} onChange={(v) => updateArray("experience", i, "role", v)} />
                    <Field label="Years" testId={`exp-years-${i}`} value={ex.years} onChange={(v) => updateArray("experience", i, "years", v)} />
                  </div>
                  <div className="mt-4">
                    <Label className="mb-1.5 block">Description</Label>
                    <Textarea data-testid={`exp-desc-${i}`} rows={3} value={ex.description} onChange={(e) => updateArray("experience", i, "description", e.target.value)} />
                  </div>
                  {form.experience.length > 1 && (
                    <Button variant="ghost" size="sm" className="text-red-600 mt-3" onClick={() => delItem("experience", i)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-primary">Declarations</h2>
              <Declaration testId="dec-truth" checked={form.declarations.truth} onChange={(v) => set("declarations.truth", v)} title="Truthfulness" body="I attest that all information submitted is true and complete to the best of my knowledge." />
              <Declaration testId="dec-ethics" checked={form.declarations.ethics} onChange={(v) => set("declarations.ethics", v)} title="Code of ethics" body="I agree to be bound by the published Code of Ethics of the issuing board and to comply with its disciplinary process." />
              <div>
                <Declaration testId="dec-disc" checked={form.declarations.disciplinary} onChange={(v) => set("declarations.disciplinary", v)} title="Prior disciplinary action" body="I have prior licensing or disciplinary action to disclose." />
                {form.declarations.disciplinary && (
                  <div className="mt-3">
                    <Label className="mb-1.5 block">Please describe</Label>
                    <Textarea data-testid="dec-disc-note" rows={3} value={form.declarations.disciplinary_note} onChange={(e) => set("declarations.disciplinary_note", e.target.value)} />
                  </div>
                )}
              </div>
              <Card className="p-4 border-slate-200 bg-slate-50 text-xs text-slate-600 leading-relaxed">
                Your payment is already verified. After submitting, you'll be taken to the application page to upload supporting documents (transcripts, ID, certificates) for reviewer evaluation.
              </Card>
            </div>
          )}

          <div className="border-t border-slate-200 mt-8 pt-5 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="step-prev">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button className="bg-primary text-white hover:bg-[#113A64]" onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="step-next">
                Continue <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-primary text-white hover:bg-[#113A64]" disabled={!canNext() || submitting} onClick={submit} data-testid="application-submit">
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", required }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </div>
  );
}

function Declaration({ checked, onChange, title, body, testId }) {
  return (
    <Card className="p-4 border-slate-200">
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
        <span>
          <span className="font-medium text-primary block">{title}</span>
          <span className="text-sm text-slate-600 leading-relaxed">{body}</span>
        </span>
      </label>
    </Card>
  );
}
